import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Bell, BellRing, BellOff, Smartphone, Info } from "lucide-react";
import { pushSupported, enablePush, disablePush, getSubscription } from "@/lib/push";

export default function NotificationSettings() {
  const [data, setData] = useState(null);
  const [pushState, setPushState] = useState({ supported: pushSupported(), permission: typeof Notification !== "undefined" ? Notification.permission : "unsupported", subscribed: false, hasSW: false });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const r = await api.get("/notifications/prefs"); setData(r.data);
    const sub = await getSubscription().catch(() => null);
    const reg = "serviceWorker" in navigator ? await navigator.serviceWorker.getRegistration() : null;
    setPushState((p) => ({ ...p, subscribed: !!sub, hasSW: !!reg, permission: typeof Notification !== "undefined" ? Notification.permission : "unsupported" }));
  };
  useEffect(() => { load(); }, []);
  if (!data) return <div className="p-8 text-slate-500">Carregando...</div>;

  const prefs = data.prefs;
  const save = async (next) => {
    try { const r = await api.put("/notifications/prefs", next); setData({ ...data, prefs: r.data.prefs }); toast.success("Preferências salvas"); } catch (e) { toast.error(errMsg(e)); }
  };
  const toggleType = (channel, id) => {
    const list = prefs[channel].includes(id) ? prefs[channel].filter((x) => x !== id) : [...prefs[channel], id];
    save({ ...prefs, [channel]: list });
  };

  const onEnable = async () => {
    setBusy(true);
    try {
      const r = await enablePush();
      if (r.ok) { await save({ ...prefs, push_enabled: true }); toast.success("Notificações push ativadas neste aparelho"); }
      else toast.error({ unsupported: "Este navegador não suporta notificações push.", "no-sw": "Instale o app ou acesse pela versão publicada (HTTPS) para ativar o push.", denied: "Permissão negada. Você pode liberar nas configurações do navegador.", server: "Push não configurado no servidor." }[r.reason] || "Não foi possível ativar.");
    } finally { setBusy(false); load(); }
  };
  const onDisable = async () => {
    if (!window.confirm("Desativar notificações push neste aparelho?")) return;
    setBusy(true);
    try { await disablePush(); await save({ ...prefs, push_enabled: false }); toast.success("Push desativado"); } finally { setBusy(false); load(); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5 sm:space-y-6">
      <PageHeader title="Notificações" subtitle="Escolha o que deseja receber no sino do sistema e no celular/computador (push)." />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 border-slate-200 dark:border-slate-800 overflow-hidden">
          <div className="grid grid-cols-[1fr_72px_72px] sm:grid-cols-[1fr_110px_110px] items-center px-5 py-3 bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 text-[11px] font-bold uppercase tracking-wider text-slate-500">
            <span>Tipo de aviso</span><span className="text-center flex items-center justify-center gap-1"><Bell className="w-3.5 h-3.5" /> Sino</span><span className="text-center flex items-center justify-center gap-1"><Smartphone className="w-3.5 h-3.5" /> Push</span>
          </div>
          {data.types.map((t) => (
            <div key={t.id} className="grid grid-cols-[1fr_72px_72px] sm:grid-cols-[1fr_110px_110px] items-center px-5 py-3 border-b border-slate-100 dark:border-slate-800 last:border-0" data-testid={`pref-row-${t.id}`}>
              <span className="text-sm font-medium text-slate-800 dark:text-slate-200">{t.name}</span>
              <span className="flex justify-center"><Switch checked={prefs.inapp.includes(t.id)} onCheckedChange={() => toggleType("inapp", t.id)} data-testid={`pref-inapp-${t.id}`} /></span>
              <span className="flex justify-center"><Switch checked={prefs.push.includes(t.id)} onCheckedChange={() => toggleType("push", t.id)} disabled={!prefs.push_enabled} data-testid={`pref-push-${t.id}`} /></span>
            </div>
          ))}
        </Card>

        <Card className="p-5 border-slate-200 dark:border-slate-800 space-y-4" data-testid="push-card">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${pushState.subscribed && prefs.push_enabled ? "bg-emerald-500/10 text-emerald-600" : "bg-slate-100 dark:bg-slate-800 text-slate-500"}`}>{pushState.subscribed && prefs.push_enabled ? <BellRing className="w-5 h-5" /> : <BellOff className="w-5 h-5" />}</div>
            <div><p className="font-bold text-sm">Push neste aparelho</p><p className="text-xs text-slate-500" data-testid="push-status-text">{!pushState.supported ? "Não suportado neste navegador" : pushState.subscribed && prefs.push_enabled ? "Ativo" : "Desativado"}</p></div>
          </div>
          <p className="text-xs text-slate-600 dark:text-slate-400 flex gap-2"><Info className="w-4 h-4 shrink-0 mt-0.5" /> Pedimos permissão para avisar sobre novas OS, mudanças de status, atribuições e respostas — mesmo com o app fechado. As mensagens são curtas (ex.: "A OS 123 mudou de status") e não mostram dados do cliente na tela bloqueada.</p>
          {pushState.subscribed && prefs.push_enabled
            ? <Button variant="outline" className="w-full" onClick={onDisable} disabled={busy} data-testid="push-disable-btn"><BellOff className="w-4 h-4 mr-2" /> Desativar push</Button>
            : <Button className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={onEnable} disabled={busy || !pushState.supported} data-testid="push-enable-btn"><BellRing className="w-4 h-4 mr-2" /> Ativar notificações</Button>}
          <div className="text-[11px] text-slate-500 space-y-1 border-t border-slate-200 dark:border-slate-800 pt-3">
            <p><b>Android/Computador (Chrome, Edge):</b> funciona no navegador e no app instalado.</p>
            <p><b>iPhone/iPad:</b> requer iOS 16.4+, o app adicionado à Tela de Início e a permissão concedida dentro do app.</p>
            <p>Sem permissão ou suporte, os avisos continuam no sino do sistema.</p>
          </div>
        </Card>
      </div>
    </div>
  );
}
